import { getCommunicationPreferences } from "api/get-communication-preferences";
import { useQuery } from "@tanstack/react-query";
import { PREFERENCE_ID_MAP, QUERY_KEYS } from "common/constants/api";
import {
  CommunicationPreference,
  CommunicationPreferencesData,
  CommunicationPreferenceSection,
  TransformedCommunicationPreferencesData,
} from "types/types";

const supportedPreferenceIds = new Set(Object.values(PREFERENCE_ID_MAP));

const coerceOptInValue = (value: unknown): boolean | undefined => {
  if (value === undefined || value === null) {
    return undefined;
  }
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "string") {
    return value === "true";
  }
  return undefined;
};

export const transformCommunicationPreferencesData = (
  apiResponse: CommunicationPreferencesData
): TransformedCommunicationPreferencesData => {
  if (!apiResponse?.data || !Array.isArray(apiResponse.data)) {
    return { data: [] };
  }

  return {
    data: apiResponse.data.map((section: CommunicationPreferenceSection) => ({
      section: section.id,
      sectionName: section.attributes?.sectionName,
      description: section.attributes?.description,
      preferences: (section.attributes?.preferences || [])
        .filter((pref: CommunicationPreference) =>
          supportedPreferenceIds.has(pref.id)
        )
        .map((pref: CommunicationPreference) => {
          const fromOptions = Object.fromEntries(
            pref.attributes?.options?.map((opt) => [opt.name, opt.value]) ?? []
          ) as Record<string, unknown>;
          const { optIn: rawOptIn, ...restOptions } = fromOptions;
          const normalizedOptIn =
            rawOptIn !== undefined ? coerceOptInValue(rawOptIn) : undefined;

          return {
            id: pref.id,
            name: pref.attributes?.name,
            description: pref.attributes?.description,
            ...restOptions,
            ...(normalizedOptIn !== undefined
              ? { optIn: normalizedOptIn }
              : {}),
          };
        }),
    })),
  };
};

export const getPreferenceInfo = (
  data: TransformedCommunicationPreferencesData | undefined,
  preferenceId: string
) => {
  if (!data?.data) {
    return undefined;
  }

  const allPreferences = data.data.flatMap((section) => section.preferences);
  return allPreferences.find((pref) => pref.id === preferenceId);
};

export const useGetCommunicationPreferences = () =>
  useQuery({
    queryKey: [QUERY_KEYS.COMMUNICATION_PREFERENCES],
    queryFn: () =>
      getCommunicationPreferences().then(transformCommunicationPreferencesData),
  });
